import { PLAN_KEYS } from "../../constants/automapDealerPlanConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useCurrency } from "../../intl";

export const UPGRADE_PLAN_ORDER = PLAN_KEYS.filter((key) => key !== "custom");

export function planRank(planKey) {
  const index = UPGRADE_PLAN_ORDER.indexOf(planKey);
  return index === -1 ? UPGRADE_PLAN_ORDER.length : index;
}

export function formatPlanPrice(plan, formatMoney, t) {
  if (plan?.monthlyPriceCop == null) {
    return t("plan.priceContact");
  }
  if (plan.monthlyPriceCop === 0) {
    return t("plan.priceFree");
  }
  return t("plan.priceMonthly", { price: formatMoney(plan.monthlyPriceCop) });
}

function FeatureRow({ emoji, label, value, formatPlanFeature, formatReportsAccess }) {
  let display = formatPlanFeature(value);
  if (value === "basic" || value === "full") {
    display = formatReportsAccess(value);
  }
  return (
    <li className="dealer-plan-feature">
      <span className="dealer-plan-feature__emoji" aria-hidden="true">
        {emoji}
      </span>
      <span className="dealer-plan-feature__label">{label}</span>
      <span className="dealer-plan-feature__value">{display}</span>
    </li>
  );
}

export default function DealerPlanTierCard({
  plan,
  isCurrent = false,
  isRecommended = false,
  onUpgrade,
  upgradeLabel,
  currentLabel,
}) {
  const { t, formatPlanLabel, formatPlanFeature, formatReportsAccess } = useDealerPortalIntl();
  const { formatMoney } = useCurrency();

  if (!plan) return null;

  return (
    <article
      className={`dealer-plan-tier${isCurrent ? " dealer-plan-tier--current" : ""}${
        isRecommended ? " dealer-plan-tier--recommended" : ""
      }`}
    >
      {isCurrent ? (
        <span className="dealer-plan-tier__badge dealer-plan-tier__badge--current">
          {currentLabel || t("plan.currentPlanBadge")}
        </span>
      ) : null}
      {!isCurrent && isRecommended ? (
        <span className="dealer-plan-tier__badge dealer-plan-tier__badge--recommended">
          {t("plan.recommendedBadge")}
        </span>
      ) : null}

      <header className="dealer-plan-tier__header">
        <span className="dealer-plan-tier__emoji" aria-hidden="true">
          {t(`plan.planEmojis.${plan.planKey}`)}
        </span>
        <div>
          <h3>{formatPlanLabel(plan.planKey, plan.publicName)}</h3>
          <p className="dealer-plan-tier__price">{formatPlanPrice(plan, formatMoney, t)}</p>
        </div>
      </header>

      <p className="dealer-plan-tier__description">{plan.description}</p>

      <ul className="dealer-plan-tier__features">
        <FeatureRow
          emoji="🚗"
          label={t("plan.colListings")}
          value={plan.listingLimit}
          formatPlanFeature={formatPlanFeature}
          formatReportsAccess={formatReportsAccess}
        />
        <FeatureRow
          emoji="📩"
          label={t("plan.colLeads")}
          value={plan.leadLimitMonthly}
          formatPlanFeature={formatPlanFeature}
          formatReportsAccess={formatReportsAccess}
        />
        <FeatureRow
          emoji="📤"
          label={t("plan.colBulk")}
          value={plan.bulkUploadAccess}
          formatPlanFeature={formatPlanFeature}
          formatReportsAccess={formatReportsAccess}
        />
        <FeatureRow
          emoji="📊"
          label={t("plan.colReports")}
          value={plan.performanceReportsAccess}
          formatPlanFeature={formatPlanFeature}
          formatReportsAccess={formatReportsAccess}
        />
        <FeatureRow
          emoji="⭐"
          label={t("plan.colFeatured")}
          value={plan.featuredPlacementEligible}
          formatPlanFeature={formatPlanFeature}
          formatReportsAccess={formatReportsAccess}
        />
        {plan.seoDealerPageEligible != null ? (
          <FeatureRow
            emoji="🔎"
            label={t("plan.featureSeoPage")}
            value={plan.seoDealerPageEligible}
            formatPlanFeature={formatPlanFeature}
            formatReportsAccess={formatReportsAccess}
          />
        ) : null}
        {plan.prioritySupport != null ? (
          <FeatureRow
            emoji="🛟"
            label={t("plan.featurePrioritySupport")}
            value={plan.prioritySupport}
            formatPlanFeature={formatPlanFeature}
            formatReportsAccess={formatReportsAccess}
          />
        ) : null}
      </ul>

      {isCurrent ? (
        <p className="dealer-plan-tier__footnote">{t("plan.currentPlanFootnote")}</p>
      ) : onUpgrade ? (
        <button type="button" className="dealer-portal-btn primary dealer-plan-tier__cta" onClick={onUpgrade}>
          {upgradeLabel || t("plan.upgradeCta")}
        </button>
      ) : null}
    </article>
  );
}

export function UsageMeter({ emoji, label, used, limit, formatPlanLimit }) {
  const pct =
    limit != null && limit > 0 ? Math.min(100, Math.round(((used ?? 0) / limit) * 100)) : null;

  return (
    <div className="dealer-plan-usage-meter">
      <div className="dealer-plan-usage-meter__head">
        <span className="dealer-plan-usage-meter__label">
          <span aria-hidden="true">{emoji}</span> {label}
        </span>
        <strong>
          {used ?? 0}/{formatPlanLimit(limit)}
        </strong>
      </div>
      {pct != null ? (
        <div className="dealer-plan-usage-meter__track" aria-hidden="true">
          <div
            className={`dealer-plan-usage-meter__fill${pct >= 90 ? " is-critical" : pct >= 75 ? " is-warn" : ""}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      ) : (
        <p className="dealer-plan-usage-meter__unlimited">{limit == null ? "∞" : null}</p>
      )}
    </div>
  );
}
